import {PAYMENT_KEYS} from "@/api/paymentApi";
import TimesIcon from "@/assets/colorable/times.svg?react";
import AdvancedIcon from "@/assets/dark_gradient/advanced_package.svg?react";
import AdvancedBorderedIcon from "@/assets/dark_gradient/advanced_package_circle.svg?react";
import EnterpriseIcon from "@/assets/dark_gradient/enterprise_package.svg?react";
import EnterpriseBorderedIcon from "@/assets/dark_gradient/enterprise_package_circle.svg?react";
import ExpertIcon from "@/assets/dark_gradient/expert_package.svg?react";
import ExpertBorderedIcon from "@/assets/dark_gradient/expert_package_circle.svg?react";
import ProfessionalIcon from "@/assets/dark_gradient/professional_package.svg?react";
import ProfessionalBorderedIcon from "@/assets/dark_gradient/professional_package_circle.svg?react";
import StarterBorderedIcon from "@/assets/dark_gradient/starter_package_circle.svg?react";
import clsxm from "@/lib/clsxm";
import {usePaymentStore} from "@/store/payment";
import type {TCurrency, TPackageCategory, TPackageName, TSubscriptionWithDiscountOptions} from "@/store/types";
import {queryOptions, useQueries} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {map} from "lodash";
import {useEffect, useState, type FC, type SVGProps} from "react";
import {useTranslation} from "react-i18next";
import {usePrevious} from "react-use";

export const packages: {
  [K in TPackageName]: {
    icon?: FC<SVGProps<SVGSVGElement>>;
    translationKey: string;
    borderedIcon: FC<SVGProps<SVGSVGElement>>;
    type: K;
  };
} = {
  NONE: {
    translationKey: "noActivePlan",
    borderedIcon: TimesIcon,
    type: "NONE",
  },
  STARTER: {
    translationKey: "packageStarter",
    borderedIcon: StarterBorderedIcon,
    type: "STARTER",
  },
  ESSENTIALS: {
    translationKey: "packageEssentials",
    borderedIcon: StarterBorderedIcon,
    type: "ESSENTIALS",
  },
  ADVANCED: {
    icon: AdvancedIcon,
    translationKey: "packageAdvanced",
    borderedIcon: AdvancedBorderedIcon,
    type: "ADVANCED",
  },
  PROFESSIONAL: {
    icon: ProfessionalIcon,
    translationKey: "packageProfessional",
    borderedIcon: ProfessionalBorderedIcon,
    type: "PROFESSIONAL",
  },
  EXPERT: {
    icon: ExpertIcon,
    translationKey: "packageExpert",
    borderedIcon: ExpertBorderedIcon,
    type: "EXPERT",
  },
  ENTERPRISE: {
    icon: EnterpriseIcon,
    translationKey: "packageTailored",
    borderedIcon: EnterpriseBorderedIcon,
    type: "ENTERPRISE",
  },
} as const;

export const getPackageIcon = ({
  className,
  packageType,
  defaultIcon,
}: {
  className?: string;
  packageType: keyof typeof packages;
  defaultIcon?: boolean;
}) => {
  const pkg = packages[packageType];

  if (!pkg) return null;

  const PackageIcon = defaultIcon && pkg.icon ? pkg.icon : pkg.borderedIcon;
  return <PackageIcon className={clsxm(className, packageType === "NONE" && "text-danger-raspberry")} />;
};

const usePackages = (currency: TCurrency, category?: TPackageCategory) => {
  const {t} = useTranslation("common");
  const setter = usePaymentStore(state => state.setter);
  const plans = useQueries({
    queries: ["INSTAGRAM", "TIKTOK", "BOTH"].map(category =>
      queryOptions<AxiosResponse<TSubscriptionWithDiscountOptions[]>, AxiosError, TSubscriptionWithDiscountOptions[]>({
        queryKey: PAYMENT_KEYS.qPackageList(`currency=${currency}&package_category=${category}`),
        select: response => response.data,
      }),
    ),
  });
  const [isPending, setIsPending] = useState(true);
  const data = usePaymentStore(state => state.getPlans(category));

  const extendedData = data
    ? [
        ...data,
        {
          id: 5,
          name: "ENTERPRISE",
          influencers_per_campaign: "unlimited",
          invitations_per_campaign: "unlimited",
          influencer_level_insights: true,
          multiple_active_campaigns: true,
          other_countries: true,
          priority_notifications_and_advertising: true,
          personal_account_manager: true,
          price_per_month: 0,
          price_per_year: 0,
          currency: currency,
          package_type: "YEARLY",
          discount_percentage: 0,
          price_per_year_with_discount: 0,
          price_per_month_with_discount: 0,
          template_name: "",
          buyout: "custom",
        } as unknown as TSubscriptionWithDiscountOptions,
      ]
    : [];
  const extendedPackagesList = map(packages, p => ({...p, name: t(p.translationKey as "noActivePlan")}));
  const packagesList = extendedPackagesList.slice(1);
  const previousPackages = usePrevious(data ?? []);
  const packagesData = previousPackages?.length && typeof data === "undefined" ? previousPackages : (data ?? []);

  useEffect(() => {
    if (plans.every(plan => plan.isSuccess)) {
      setter(state => {
        state.instagram = plans[0].data!;
        state.tiktok = plans[1].data!;
        state.both = plans[2].data!;
      });

      setIsPending(false);
    }
  }, [plans, setter, setIsPending]);

  return {
    isPending,
    extendedPackagesList,
    packagesData,
    packagesList,
    packages,
    extendedData,
  };
};

export default usePackages;
